import { VimplayGame } from "./VimplayGame.tsx";

export type VimplayLobbyCategoryId =
  | "cards"
  | "roulette"
  | "keno"
  | "crash"
  | "mines"
  | "plinko"
  | "more";

export interface VimplayLobbyCategory {
  id: VimplayLobbyCategoryId;
  label: string;
}

export const VIMPLAY_LOBBY_CATEGORIES: VimplayLobbyCategory[] = [
  { id: "cards", label: "Blackjack / Cards" },
  { id: "roulette", label: "Roulette" },
  { id: "keno", label: "Keno" },
  { id: "crash", label: "Crash" },
  { id: "mines", label: "Mines & Step" },
  { id: "plinko", label: "Plinko" },
  { id: "more", label: "More Games" },
];

const CATEGORY_NAMES: Record<VimplayLobbyCategoryId, string[]> = {
  cards: [
    "Blackjack",
    "Blackjack XXI 1",
    "Blackjack XXI 2",
    "Baccarat 1",
    "Baccarat 2",
    "Dragon Tiger",
  ],
  roulette: [
    "Arabic Roulette",
    "Cosmic Roulette",
    "Imperial Roulette",
    "Midnight Roulette",
    "Monaco Roulette",
    "Replay Roulette",
    "Royal Roulette",
    "Sakura Roulette",
    "Spanish Roulette",
    "Techno Roulette",
    "Rouletto",
    "Spinzo Roulette",
    "Tiny Roulette",
  ],
  keno: [
    "Afro Keno",
    "Aqua Keno",
    "Grand Keno",
    "Sahara Keno",
    "Fast keno",
    "Keno X",
  ],
  crash: [
    "Captain Lion",
    "Crash Kick",
    "Helichrash",
    "Magic Aladdin",
    "Shaktimaan",
  ],
  mines: [
    "Bomb Runner",
    "Chickn Road",
    "Chickn Road2",
    "Diamond Mine",
    "Fate Flip",
    "Hand Duel",
    "Just Jump",
    "Lotto Luck",
    "Paradice",
    "Pirate Mines",
    "Rock Star",
    "The Skyscraper",
    "Toss Boss",
    "Treasure Hunt",
  ],
  plinko: [
    "Harley's Plinko",
    "Plinko Temple",
  ],
  more: [
    "Aqua Multiplier",
    "Bonbon Kingdom",
    "Crazy Tiki",
    "Dragon Tap",
    "Elementals",
    "Find Trace",
    "Gem Royale",
    "Hot or Cold",
    "Joker's Choice",
    "Joker Blitz",
    "Lucky Harvest",
    "Lucky Line",
    "Magic Box",
    "Magic Spell",
    "Monster Lab",
    "Ocean Deep",
    "Odyssey of Gods",
    "Ong Bet",
    "Pharaoh's Secrets",
    "Royal Dice",
    "Safari king",
    "Space Raiders",
    "Space Y",
    "Spinergy",
    "Spinny Winny",
    "Spinora",
    "Super Nico",
    "Turbo Multiplier",
    "Wishing Well",
    "Color Wheel",
    "Greyhound Racing",
    "PenaltX",
  ],
};

const LIVE_NAMES = [
  "Blackjack XXI 1",
  "Blackjack XXI 2",
  "Baccarat 1",
  "Baccarat 2",
  "Dragon Tiger",
  "Arabic Roulette",
  "Cosmic Roulette",
  "Imperial Roulette",
  "Midnight Roulette",
  "Monaco Roulette",
  "Replay Roulette",
  "Royal Roulette",
  "Sakura Roulette",
  "Spanish Roulette",
  "Techno Roulette",
];

export function normalizeVimplayName(name: string | null | undefined): string {
  return (name ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

const nameToCategory = new Map<string, VimplayLobbyCategoryId>();
const categoryOrder = new Map<VimplayLobbyCategoryId, string[]>();

for (const category of VIMPLAY_LOBBY_CATEGORIES) {
  const names = CATEGORY_NAMES[category.id].map(normalizeVimplayName);
  categoryOrder.set(category.id, names);
  for (const name of names) nameToCategory.set(name, category.id);
}

const liveNames = new Set(LIVE_NAMES.map(normalizeVimplayName));

export function vimplayLobbyCategory(game: Pick<VimplayGame, "name">): VimplayLobbyCategory {
  const id = nameToCategory.get(normalizeVimplayName(game.name)) ?? "more";
  return VIMPLAY_LOBBY_CATEGORIES.find((category) => category.id === id) ?? VIMPLAY_LOBBY_CATEGORIES[6];
}

export function compareVimplayLobbyGames(
  categoryId: VimplayLobbyCategoryId,
  a: Pick<VimplayGame, "name">,
  b: Pick<VimplayGame, "name">,
): number {
  const order = categoryOrder.get(categoryId) ?? [];
  const rank = (game: Pick<VimplayGame, "name">) => {
    const index = order.indexOf(normalizeVimplayName(game.name));
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  const difference = rank(a) - rank(b);
  if (difference !== 0) return difference;
  return normalizeVimplayName(a.name).localeCompare(normalizeVimplayName(b.name));
}

export function vimplayGameIsLive(game: Pick<VimplayGame, "name" | "type">): boolean {
  const type = (game.type ?? "").trim().toLowerCase();
  if (type) return type === "live";
  return liveNames.has(normalizeVimplayName(game.name));
}
